import {
  faPencil,
  faPlus,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IoIosSearch } from "react-icons/io";
import { HiLink } from "react-icons/hi2";
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { showToast } from "../../components/toast";
import { PermissionWrapper } from "../../helpers/permission.js";

const DynamicBlogsList = () => {
  const [dynamicBlogs, setDynamicBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [requestingId, setRequestingId] = useState(0);
  const navigate = useNavigate();
  const toastShownRef = useRef(false);

  // Search filter states
  const [postTitleFilter, setPostTitleFilter] = useState("");
  const [shortcodeFilter, setShortcodeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fetchedOnFilter, setFetchedOnFilter] = useState("");
  const [requestedOnFilter, setRequestedOnFilter] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  // Fetch dynamic blogs on component mount
  useEffect(() => {
    fetchDynamicBlogs();
  }, []);

  const fetchDynamicBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("authToken");

      const response = await axios.get(`${API_URL}dynamicBlogs/dynamic-blogs`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log("API Response:", response.data);
      if (!toastShownRef.current) {
        showToast.success("Dynamic Blogs fetched successfully");
        toastShownRef.current = true;
      }

      let blogsData = [];
      if (
        response.data &&
        response.data.dynamicBlogs &&
        Array.isArray(response.data.dynamicBlogs)
      ) {
        blogsData = response.data.dynamicBlogs;
      } else if (response.data && response.data.success && response.data.data) {
        blogsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        blogsData = response.data;
      } else {
        blogsData = response.data?.data || response.data || [];
      }

      setDynamicBlogs(blogsData);
      setTotal(blogsData.length);
      setPageCount(Math.ceil(blogsData.length / limit));
      setLoading(false);
    } catch (err) {
      console.error("Error fetching dynamic blogs:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to fetch dynamic blogs. Please try again later.";
      setError(errorMessage);
      showToast.error(errorMessage);
      setLoading(false);
    }
  };

  // Filter blogs based on search criteria
  const filteredBlogs = useMemo(() => {
    return dynamicBlogs.filter((blog) => {
      // Post Title filter
      if (
        postTitleFilter &&
        !blog.post_title?.toLowerCase().includes(postTitleFilter.toLowerCase())
      ) {
        return false;
      }

      // Shortcode filter
      if (
        shortcodeFilter &&
        !blog.shortcode_key
          ?.toLowerCase()
          .includes(shortcodeFilter.toLowerCase())
      ) {
        return false;
      }

      // Status filter
      if (
        statusFilter &&
        !blog.status?.toLowerCase().includes(statusFilter.toLowerCase())
      ) {
        return false;
      }

      // Fetched On filter
      if (fetchedOnFilter) {
        const fetchedDate = new Date(blog.fetched_on);
        const filterDate = new Date(fetchedOnFilter);

        if (isNaN(fetchedDate.getTime())) return false;

        // Check if dates match (ignoring time)
        if (
          fetchedDate.getFullYear() !== filterDate.getFullYear() ||
          fetchedDate.getMonth() !== filterDate.getMonth() ||
          fetchedDate.getDate() !== filterDate.getDate()
        ) {
          return false;
        }
      }

      // Requested On filter
      if (requestedOnFilter) {
        const requestedDate = new Date(blog.requested_on);
        const filterDate = new Date(requestedOnFilter);

        if (isNaN(requestedDate.getTime())) return false;

        // Check if dates match (ignoring time)
        if (
          requestedDate.getFullYear() !== filterDate.getFullYear() ||
          requestedDate.getMonth() !== filterDate.getMonth() ||
          requestedDate.getDate() !== filterDate.getDate()
        ) {
          return false;
        }
      }

      return true;
    });
  }, [dynamicBlogs, postTitleFilter, shortcodeFilter, statusFilter, fetchedOnFilter, requestedOnFilter]);

  // Update pagination based on filtered results
  useEffect(() => {
    setTotal(filteredBlogs.length);
    setPageCount(Math.ceil(filteredBlogs.length / limit));
    setPage(1); // Reset to first page when filters change
  }, [filteredBlogs, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  useEffect(() => {
    if (!dynamicBlogs.length) return;

    setIsFiltering(true);

    const timer = setTimeout(() => {
      setIsFiltering(false);
    }, 300); // smooth UX delay

    return () => clearTimeout(timer);
  }, [
    postTitleFilter,
    shortcodeFilter,
    statusFilter,
    fetchedOnFilter,
    requestedOnFilter,
  ]);

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedBlogs = filteredBlogs.slice(start - 1, end);

  // Handle Get Data button click
  const handleGetData = async () => {
    try {
      setUpdating(true);
      const token = localStorage.getItem("authToken");

      const response = await axios.post(
        `${API_URL}dynamicBlogs/update-dynamic-blogs`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      console.log("Update response:", response.data);
      showToast.success("Dynamic Blog List Updated Successfully");
      fetchDynamicBlogs();
      setUpdating(false);
    } catch (err) {
      console.error("Error updating dynamic blogs:", err);
      setUpdating(false);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to update dynamic blogs. Please try again.";
      showToast.error(errorMessage);
    }
  };

  // Handle view post functionality
  const handleViewPost = (postLink) => {
    if (postLink) {
      window.open(postLink, "_blank", "noopener,noreferrer");
    } else {
      showToast.warning("Post link not available");
    }
  };

  // Handle request content functionality
  const handleRequestContent = async (id) => {
    try {
      setRequestingId(id);
      const token = localStorage.getItem("authToken");

      const response = await axios.post(
        `${API_URL}dynamicBlogs/request-content/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      console.log("Request content response:", response.data);
      showToast.success("Requested Successfully");
      fetchDynamicBlogs();
    } catch (err) {
      console.error("Error requesting content:", err);
      const errorMessage =
        err.response?.data?.message ||
        "Failed to request content. Please try again.";
      showToast.error(errorMessage);
    } finally {
      setRequestingId(0);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge badge-secondary"></span>;

    const statusClass = {
      active: "badge-success",
      inactive: "badge-danger",
      pending: "badge-warning",
      draft: "badge-info",
    };

    return (
      <span
        className={`badge ${statusClass[status.toLowerCase()] || "badge-secondary"
          }`}
      >
        {status}
      </span>
    );
  };

  const handleRetry = () => {
    setError(null);
    fetchDynamicBlogs();
  };
  useEffect(() => {
    const handleClearFilters = () => {
      setPostTitleFilter("");
      setShortcodeFilter("");
      setStatusFilter("");
      setFetchedOnFilter("");
      setRequestedOnFilter("");
    };
    window.addEventListener('clearDynamicBlogFilterEvent', handleClearFilters);

    return () => {
      window.removeEventListener('clearDynamicBlogFilterEvent', handleClearFilters);
    };
  }, []);
  return (
    <div className="page-container mt-5">
      {/* {loading && <LoadingSpinner />} */}
      <div className="">
        {error && (
          <div className="alert alert-danger">
            {error}
            <button
              className="btn btn-sm btn-outline-danger ml-2"
              onClick={handleRetry}
            >
              Retry
            </button>
          </div>
        )}

        <div className="panel panel-default with-rounded-corner with-shadow p-10">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="panel-heading border-0">
              <h4 className="panel-title">Manage Dynamic Blogs</h4>
            </div>
            <div className="d-flex align-items-center">
              <button
                className="btn btn-default btn-sm"
                onClick={handleGetData}
                disabled={updating}
              >
                {updating ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlus} /> Get Data
                  </>
                )}
              </button>
              {/* Pagination Controls */}
              {dynamicBlogs.length > 0 && (
                <div className="d-inline-block me-3 ms-3">
                  <span style={{ fontSize: "0.8rem" }} className="me-2">
                    {start} to {end} of {total}
                  </span>
                  <button
                    className="btn btn-default btn-sm"
                    title="Previous"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  &nbsp;
                  <button
                    className="btn btn-default btn-sm"
                    title="Next"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === pageCount}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {loading || dynamicBlogs.length > 0 ? (
            <div className="table-responsive user-table-container">
              <style>
                {`
                  .user-table-container {
                    position: relative;
                    max-height: 71vh;
                    overflow-y: auto;
                    overflow-x: auto;
                   }

                  /* First header row (titles) */
                  .user-table-container table thead tr:nth-child(1) th {
                    position: sticky;
                    top: -1px;
                    z-index: 30;
                    background: #f8f9fa;
                  }

                 /* Second header row (search inputs) */
                 .user-table-container table thead tr:nth-child(2) th {
                   position: sticky;
                   top: 25px; /* adjust based on header height */
                   z-index: 25;
                   background: #fff;
                  }
               `}
              </style>
              <table className="table table-condensed table-bordered bg-white">
                <thead>
                  <tr>
                    <th style={{ textAlign: "center" }}>#</th>
                    <th>Post Title</th>
                    <th>Shortcode</th>
                    <th>Fetched On</th>
                    <th>Content Updated On</th>
                    <th>Status</th>
                    <th>Requested On</th>
                    <th>Action</th>
                  </tr>
                  <tr>
                    <th style={{ textAlign: "center" }}></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search title..."
                        value={postTitleFilter}
                        onChange={(e) => setPostTitleFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search shortcode..."
                        value={shortcodeFilter}
                        onChange={(e) => setShortcodeFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="date"
                        className="form-control input-sm"
                        value={fetchedOnFilter}
                        onChange={(e) => setFetchedOnFilter(e.target.value)}
                      />
                    </th>
                    <th></th>
                    <th>
                      <input
                        type="text"
                        className="form-control input-sm"
                        placeholder="Search status..."
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <input
                        type="date"
                        className="form-control input-sm"
                        value={requestedOnFilter}
                        onChange={(e) => setRequestedOnFilter(e.target.value)}
                      />
                    </th>
                    <th>
                      <button
                        className="btn btn-default btn-sm"
                        onClick={() => {
                          setPostTitleFilter("");
                          setShortcodeFilter("");
                          setStatusFilter("");
                          setFetchedOnFilter("");
                          setRequestedOnFilter("");
                        }}
                        title="Clear filters"
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(loading || isFiltering) ? (
                    Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i}>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                        <td><div className="skeleton-box" /></td>
                      </tr>
                    ))
                  ) : paginatedBlogs.length > 0 ? (
                    paginatedBlogs.map((blog, index) => (
                      <tr key={blog.id}>
                        <td
                          style={{
                            textAlign: "center",
                            verticalAlign: "middle",
                          }}
                        >
                          {(page - 1) * limit + index + 1}
                        </td>
                        <td>
                          <div style={{ maxWidth: "200px" }}>
                            <div
                              className="text-truncate"
                              title={blog.post_title}
                            >
                              {blog.post_title}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span>{blog.shortcode_key || "N/A"}</span>
                        </td>
                        <td>{formatDate(blog.fetched_on)}</td>
                        <td>{formatDate(blog.content_updated_on)}</td>
                        <td>{getStatusBadge(blog.status)}</td>
                        <td>{formatDate(blog.requested_on)}</td>
                        <td>
                          <PermissionWrapper module="dynamic_blogs" action="edit">

                            <button
                              className="mr-2 btn btn-warning btn-xs me-2"
                              onClick={() =>
                                navigate(`/DynamicBlogs/edit/${blog.id}`)
                              }
                              title="Edit"
                            >
                              <FontAwesomeIcon icon={faPencil} />
                            </button>
                          </PermissionWrapper>

                          <button
                            className="mr-2 btn btn-xs me-2"
                            onClick={() => handleViewPost(blog.post_link)}
                            title="View Post"
                            style={{
                              backgroundColor: "#007bff",
                              borderColor: "#007bff",
                              color: "white",
                            }}
                          >
                            <IoIosSearch />
                          </button>
                          <button
                            className="btn btn-secondary btn-xs"
                            onClick={() => handleRequestContent(blog.id)}
                            title="Request Content"
                            disabled={requestingId === blog.id}
                          >
                            {requestingId === blog.id ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              <HiLink />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center">
                        No blogs match your search criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            !loading && (
              <div className="text-center py-4">
                <p>No records found</p>
              </div>
            )
          )}
        </div>
      </div>

      {/* Scroll to top button */}
      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default DynamicBlogsList;